import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar, Line, Pie, Doughnut, Radar, Scatter, PolarArea } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Title,
  Tooltip,
  Legend
)

const ChartVisualization = ({ element }) => {
  const chartRef = useRef(null)

  // Parse chart data from element
  const parseChartData = (element) => {
    try {
      let data = element.data
      
      // If data is string, try to parse it
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data)
        } catch {
          // If parsing fails, create data from string
          return {
            labels: ['Data'],
            datasets: [{
              label: element.title || 'Data',
              data: [1],
              backgroundColor: 'rgba(147, 51, 234, 0.8)',
              borderColor: 'rgba(147, 51, 234, 1)',
              borderWidth: 1
            }]
          }
        }
      }

      // If no data, create sample data based on element info
      if (!data) {
        const title = element.title || element.description || 'Sample Data'
        return {
          labels: ['Item 1', 'Item 2', 'Item 3', 'Item 4'],
          datasets: [{
            label: title,
            data: [25, 35, 20, 20],
            backgroundColor: [
              'rgba(147, 51, 234, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(245, 158, 11, 0.8)'
            ],
            borderColor: [
              'rgba(147, 51, 234, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(245, 158, 11, 1)'
            ],
            borderWidth: 1
          }]
        }
      }

      // Handle different data structures
      if (data.labels && data.datasets) {
        return data
      }

      if (data.labels && data.values) {
        return {
          labels: data.labels,
          datasets: [{
            label: element.title || 'Data',
            data: data.values,
            backgroundColor: [
              'rgba(147, 51, 234, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(245, 158, 11, 0.8)',
              'rgba(239, 68, 68, 0.8)',
              'rgba(139, 92, 246, 0.8)'
            ],
            borderColor: [
              'rgba(147, 51, 234, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(239, 68, 68, 1)',
              'rgba(139, 92, 246, 1)'
            ],
            borderWidth: 1
          }]
        }
      }

      if (data.categories && data.values) {
        return {
          labels: data.categories,
          datasets: [{
            label: element.title || 'Data',
            data: data.values,
            backgroundColor: [
              'rgba(147, 51, 234, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(245, 158, 11, 0.8)',
              'rgba(239, 68, 68, 0.8)',
              'rgba(139, 92, 246, 0.8)'
            ],
            borderColor: [
              'rgba(147, 51, 234, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(239, 68, 68, 1)',
              'rgba(139, 92, 246, 1)'
            ],
            borderWidth: 1
          }]
        }
      }

      // Handle array of objects
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
        const keys = Object.keys(data[0])
        const labelKey = keys.find(key => 
          key.toLowerCase().includes('name') || 
          key.toLowerCase().includes('label') ||
          key.toLowerCase().includes('category') ||
          key.toLowerCase().includes('item')
        ) || keys[0]
        
        const valueKey = keys.find(key => 
          key.toLowerCase().includes('value') ||
          key.toLowerCase().includes('amount') ||
          key.toLowerCase().includes('count') ||
          key.toLowerCase().includes('data') ||
          key.toLowerCase().includes('number')
        ) || keys[1] || keys[0]

        return {
          labels: data.map(item => item[labelKey] || `Item ${data.indexOf(item) + 1}`),
          datasets: [{
            label: element.title || 'Data',
            data: data.map(item => {
              const value = item[valueKey]
              return typeof value === 'number' ? value : parseFloat(value) || Math.floor(Math.random() * 100)
            }),
            backgroundColor: [
              'rgba(147, 51, 234, 0.8)',
              'rgba(59, 130, 246, 0.8)',
              'rgba(16, 185, 129, 0.8)',
              'rgba(245, 158, 11, 0.8)',
              'rgba(239, 68, 68, 0.8)',
              'rgba(139, 92, 246, 0.8)'
            ],
            borderColor: [
              'rgba(147, 51, 234, 1)',
              'rgba(59, 130, 246, 1)',
              'rgba(16, 185, 129, 1)',
              'rgba(245, 158, 11, 1)',
              'rgba(239, 68, 68, 1)',
              'rgba(139, 92, 246, 1)'
            ],
            borderWidth: 1
          }]
        }
      }

      // Handle simple array of numbers
      if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'number') {
        return {
          labels: data.map((_, index) => `Item ${index + 1}`),
          datasets: [{
            label: element.title || 'Data',
            data: data,
            backgroundColor: 'rgba(147, 51, 234, 0.8)',
            borderColor: 'rgba(147, 51, 234, 1)',
            borderWidth: 1
          }]
        }
      }

      // Fallback: create sample data based on element description
      const sampleData = {
        labels: ['Kategori A', 'Kategori B', 'Kategori C', 'Kategori D'],
        datasets: [{
          label: element.title || 'Sample Data',
          data: [30, 25, 25, 20],
          backgroundColor: [
            'rgba(147, 51, 234, 0.8)',
            'rgba(59, 130, 246, 0.8)',
            'rgba(16, 185, 129, 0.8)',
            'rgba(245, 158, 11, 0.8)'
          ],
          borderColor: [
            'rgba(147, 51, 234, 1)',
            'rgba(59, 130, 246, 1)',
            'rgba(16, 185, 129, 1)',
            'rgba(245, 158, 11, 1)'
          ],
          borderWidth: 1
        }]
      }
      
      console.log('Using fallback chart data for:', element.title)
      return sampleData
    } catch (error) {
      console.error('Error parsing chart data:', error)
      return {
        labels: ['Data Sample'],
        datasets: [{
          label: element.title || 'Chart',
          data: [100],
          backgroundColor: 'rgba(147, 51, 234, 0.8)',
          borderColor: 'rgba(147, 51, 234, 1)',
          borderWidth: 1
        }]
      }
    }
  }

  const chartData = parseChartData(element)
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#9CA3AF'
        }
      },
      title: {
        display: true,
        text: element.title || 'Chart',
        color: '#F3F4F6'
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.9)',
        titleColor: '#F3F4F6',
        bodyColor: '#D1D5DB',
        borderColor: 'rgba(107, 114, 128, 0.2)',
        borderWidth: 1
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#9CA3AF'
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)'
        }
      },
      y: {
        ticks: {
          color: '#9CA3AF'
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)'
        }
      }
    }
  }

  // Determine chart type with more variety based on PptxGenJS capabilities
  const getChartType = (element) => {
    // First check if chartType is explicitly provided
    if (element.chartType) {
      return element.chartType;
    }
    
    const description = (element.description || '').toLowerCase();
    const title = (element.title || '').toLowerCase();
    const text = `${description} ${title}`;
    
    // Prioritas berdasarkan kata kunci spesifik
    if (text.includes('radar') || text.includes('spider') || text.includes('multi-dimensional') || text.includes('kompetitif') || text.includes('360')) {
      return 'radar';
    }
    if (text.includes('scatter') || text.includes('bubble') || text.includes('positioning') || text.includes('correlation') || text.includes('matrix')) {
      return 'scatter';
    }
    if (text.includes('area') || text.includes('timeline') || text.includes('evolution') || text.includes('adoption') || text.includes('growth')) {
      return 'area';
    }
    if (text.includes('polar') || text.includes('segmentasi') || text.includes('distribution') || text.includes('demografi')) {
      return 'polarArea';
    }
    if (text.includes('pie') || text.includes('lingkaran') || text.includes('proporsi') || text.includes('share')) {
      return 'pie';
    }
    if (text.includes('doughnut') || text.includes('donat') || text.includes('berlubang') || text.includes('nested')) {
      return 'doughnut';
    }
    if (text.includes('line') || text.includes('garis') || text.includes('trend') || text.includes('waktu') || text.includes('time')) {
      return 'line';
    }
    
    return 'bar'; // default
  };

  const chartType = getChartType(element)

  // Enhanced chart options for different chart types
  const getEnhancedOptions = (type) => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          labels: {
            color: '#9CA3AF',
            usePointStyle: true,
            padding: 20
          }
        },
        title: {
          display: true,
          text: element.title || 'Chart',
          color: '#F3F4F6',
          font: {
            size: 16,
            weight: 'bold'
          }
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.95)',
          titleColor: '#F3F4F6',
          bodyColor: '#D1D5DB',
          borderColor: 'rgba(107, 114, 128, 0.3)',
          borderWidth: 1,
          cornerRadius: 8,
          displayColors: true
        }
      }
    }

    switch (type) {
      case 'radar':
        return {
          ...baseOptions,
          scales: {
            r: {
              angleLines: {
                color: 'rgba(107, 114, 128, 0.3)'
              },
              grid: {
                color: 'rgba(107, 114, 128, 0.2)'
              },
              pointLabels: {
                color: '#9CA3AF',
                font: {
                  size: 12
                }
              },
              ticks: {
                color: '#9CA3AF',
                backdropColor: 'transparent'
              }
            }
          }
        }
      case 'scatter':
        return {
          ...baseOptions,
          scales: {
            x: {
              type: 'linear',
              position: 'bottom',
              ticks: {
                color: '#9CA3AF'
              },
              grid: {
                color: 'rgba(107, 114, 128, 0.1)'
              }
            },
            y: {
              ticks: {
                color: '#9CA3AF'
              },
              grid: {
                color: 'rgba(107, 114, 128, 0.1)'
              }
            }
          }
        }
      case 'area':
      case 'line':
        return {
          ...baseOptions,
          elements: {
            line: {
              tension: 0.4,
              borderWidth: 3
            },
            point: {
              radius: 6,
              hoverRadius: 8
            }
          },
          scales: {
            x: {
              ticks: {
                color: '#9CA3AF'
              },
              grid: {
                color: 'rgba(107, 114, 128, 0.1)'
              }
            },
            y: {
              ticks: {
                color: '#9CA3AF'
              },
              grid: {
                color: 'rgba(107, 114, 128, 0.1)'
              }
            }
          }
        }
      case 'bar':
        return {
          ...baseOptions,
          scales: {
            x: {
              ticks: {
                color: '#9CA3AF'
              },
              grid: {
                color: 'rgba(107, 114, 128, 0.1)'
              }
            },
            y: {
              ticks: {
                color: '#9CA3AF'
              },
              grid: {
                color: 'rgba(107, 114, 128, 0.1)'
              }
            }
          }
        }
      default:
        return baseOptions
    }
  }

  // Enhanced data for different chart types
  const getEnhancedData = (type) => {
    const baseData = { ...chartData }
    
    switch (type) {
      case 'radar':
        return {
          ...baseData,
          datasets: baseData.datasets.map((dataset, index) => ({
            ...dataset,
            backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.2)`,
            borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
            borderWidth: 2,
            pointBackgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`
          }))
        }
      case 'scatter':
        return {
          datasets: baseData.datasets.map((dataset, index) => ({
            ...dataset,
            data: dataset.data.map((value, i) => ({
              x: value + Math.random() * 20 - 10,
              y: Math.random() * 100
            })),
            backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.6)`,
            borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
            pointRadius: 8,
            pointHoverRadius: 10
          }))
        }
      case 'area':
        return {
          ...baseData,
          datasets: baseData.datasets.map((dataset, index) => ({
            ...dataset,
            fill: true,
            backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.3)`,
            borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
            borderWidth: 3,
            tension: 0.4
          }))
        }
      case 'line':
        return {
          ...baseData,
          datasets: baseData.datasets.map((dataset, index) => ({
            ...dataset,
            borderColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
            backgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 0.1)`,
            borderWidth: 3,
            tension: 0.4,
            pointBackgroundColor: `rgba(${index === 0 ? '147, 51, 234' : index === 1 ? '59, 130, 246' : '16, 185, 129'}, 1)`,
            pointBorderColor: '#fff',
            pointBorderWidth: 2
          }))
        }
      default:
        return baseData
    }
  }

  const enhancedOptions = getEnhancedOptions(chartType)
  const enhancedData = getEnhancedData(chartType)

  const renderChart = () => {
    const commonProps = {
      ref: chartRef,
      data: enhancedData,
      options: enhancedOptions
    }
    const chartKey = `${chartType}-${element.title || 'chart'}`

    switch (chartType) {
      case 'radar':
        return <Radar key={chartKey} {...commonProps} />
      case 'scatter':
        return <Scatter key={chartKey} {...commonProps} />
      case 'area':
        // Area chart is essentially a line chart with fill
        const areaData = {
          ...enhancedData,
          datasets: enhancedData.datasets.map(dataset => ({
            ...dataset,
            fill: true,
            backgroundColor: dataset.backgroundColor || 'rgba(54, 162, 235, 0.2)',
            borderColor: dataset.borderColor || 'rgba(54, 162, 235, 1)',
          }))
        }
        return <Line key={chartKey} {...commonProps} data={areaData} />
      case 'polarArea':
        return <PolarArea key={chartKey} {...commonProps} />
      case 'pie':
        return <Pie key={chartKey} {...commonProps} />
      case 'doughnut':
        return <Doughnut key={chartKey} {...commonProps} />
      case 'line':
        return <Line key={chartKey} {...commonProps} />
      default:
        return <Bar key={chartKey} {...commonProps} />
    }
  }

  return (
    <div className="w-full h-64 bg-bg-primary rounded-lg p-4 border border-border-primary relative overflow-hidden">
      {/* Chart type indicator */}
      <div className="absolute top-2 right-2 z-10">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          chartType === 'radar' ? 'bg-purple-500/20 text-purple-400' :
          chartType === 'scatter' ? 'bg-blue-500/20 text-blue-400' :
          chartType === 'area' ? 'bg-green-500/20 text-green-400' :
          chartType === 'polarArea' ? 'bg-pink-500/20 text-pink-400' :
          chartType === 'pie' ? 'bg-orange-500/20 text-orange-400' :
          chartType === 'doughnut' ? 'bg-cyan-500/20 text-cyan-400' :
          chartType === 'line' ? 'bg-indigo-500/20 text-indigo-400' :
          'bg-gray-500/20 text-gray-400'
        }`}>
          {chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart
        </span>
      </div>
      
      {/* Background pattern for visual enhancement */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-16 h-16 bg-purple-500 rounded-full -translate-x-8 -translate-y-8" />
        <div className="absolute bottom-0 right-0 w-12 h-12 bg-blue-500 rounded-full translate-x-6 translate-y-6" />
      </div>
      
      <div className="relative z-10 h-full">
        {renderChart()}
      </div>
    </div>
  )
}

ChartVisualization.propTypes = {
  element: PropTypes.shape({
    type: PropTypes.string.isRequired,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    data: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
    chartType: PropTypes.string,
    imageUrl: PropTypes.string // For linkup service images
  }).isRequired
}

export default ChartVisualization